/**
 * Vital Signs Store - Healthcare Vital Signs Management
 *
 * Manages patient vital signs data, history, and vital signs operations.
 * Provides hooks for vital signs state and actions.
 */

import { createAsyncActions, createStore, withLogging } from "../../core";
import type { VitalSigns, VitalSignsStoreState } from "../../types/healthcare";

// Initial state
const initialState: Omit<VitalSignsStoreState, keyof VitalSignsStoreActions> = {
  currentVitalSigns: null,
  vitalSignsHistory: [],
  selectedVitalSignsId: null,
  loading: false,
  error: null,
  _initialized: false,
  _lastUpdated: Date.now(),
};

// Actions interface
interface VitalSignsStoreActions {
  setCurrentVitalSigns: (vitalSigns: VitalSigns | null) => void;
  setVitalSignsHistory: (history: VitalSigns[]) => void;
  setSelectedVitalSignsId: (id: string | null) => void;
  addVitalSigns: (vitalSigns: VitalSigns) => void;
  updateVitalSigns: (id: string, updates: Partial<VitalSigns>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

// Complete store state
type CompleteVitalSignsStoreState = VitalSignsStoreState & VitalSignsStoreActions;

// Create the store
const useVitalSignsStore = createStore<CompleteVitalSignsStoreState>(
  withLogging("VitalSignsStore", (set) => ({
    ...initialState,
    ...createAsyncActions(),

    // Async actions implementation
    setLoading: (loading: boolean) =>
      set(() => ({
        loading,
        _lastUpdated: Date.now(),
      })),

    setError: (error: string | null) =>
      set(() => ({
        error,
        _lastUpdated: Date.now(),
      })),

    clearError: () =>
      set(() => ({
        error: null,
        _lastUpdated: Date.now(),
      })),

    // Vital signs management actions
    setCurrentVitalSigns: (vitalSigns) =>
      set(() => ({
        currentVitalSigns: vitalSigns,
        selectedVitalSignsId: vitalSigns?.id || null,
        _lastUpdated: Date.now(),
      })),

    setVitalSignsHistory: (history) =>
      set(() => ({
        vitalSignsHistory: history,
        _lastUpdated: Date.now(),
      })),

    setSelectedVitalSignsId: (id) =>
      set((state) => {
        const vitalSigns = state.vitalSignsHistory.find((vs) => vs.id === id) || null;
        return {
          selectedVitalSignsId: id,
          currentVitalSigns: vitalSigns,
          _lastUpdated: Date.now(),
        };
      }),

    addVitalSigns: (vitalSigns) =>
      set((state) => ({
        vitalSignsHistory: [...state.vitalSignsHistory, vitalSigns],
        currentVitalSigns: vitalSigns, // Set as current
        selectedVitalSignsId: vitalSigns.id,
        _lastUpdated: Date.now(),
      })),

    updateVitalSigns: (id, updates) =>
      set((state) => ({
        vitalSignsHistory: state.vitalSignsHistory.map((vs) =>
          vs.id === id ? { ...vs, ...updates } : vs,
        ),
        currentVitalSigns:
          state.currentVitalSigns?.id === id
            ? { ...state.currentVitalSigns, ...updates }
            : state.currentVitalSigns,
        _lastUpdated: Date.now(),
      })),

    reset: () =>
      set(() => ({
        ...initialState,
        _lastUpdated: Date.now(),
      })),
  })),
  {
    name: "vital-signs-storage",
    // Not persisted (ECHO-2318): vital signs must not be written to localStorage. The vital
    // sign panel refetches the current patient's records on mount.
    persist: false,
  },
);

// ===== SELECTORS =====

// Data selectors
export const useVitalSigns = () => useVitalSignsStore((state) => state);
export const useCurrentVitalSigns = () => useVitalSignsStore((state) => state.currentVitalSigns);
export const useVitalSignsHistory = () => useVitalSignsStore((state) => state.vitalSignsHistory);
export const useSelectedVitalSignsId = () =>
  useVitalSignsStore((state) => state.selectedVitalSignsId);

// State selectors
export const useVitalSignsLoading = () => useVitalSignsStore((state) => state.loading);
export const useVitalSignsError = () => useVitalSignsStore((state) => state.error);

// Computed selectors
export const useHasVitalSigns = () => useVitalSignsStore((state) => !!state.currentVitalSigns);
export const useVitalSignsHistoryCount = () =>
  useVitalSignsStore((state) => state.vitalSignsHistory.length);

// Find vital signs by ID
export const useVitalSignsById = (id: string | null) =>
  useVitalSignsStore((state) =>
    id ? state.vitalSignsHistory.find((vs) => vs.id === id) || null : null,
  );

// Get latest vital signs for a patient
export const useLatestVitalSignsForPatient = (patientId: string | null) =>
  useVitalSignsStore((state) => {
    if (!patientId) return null;
    const patientVitalSigns = state.vitalSignsHistory
      .filter((vs) => vs.patientId === patientId)
      .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime());
    return patientVitalSigns[0] || null;
  });

// Get vital signs history for a patient
export const useVitalSignsHistoryForPatient = (patientId: string | null) =>
  useVitalSignsStore((state) => {
    if (!patientId) return [];
    return state.vitalSignsHistory
      .filter((vs) => vs.patientId === patientId)
      .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime());
  });

// Individual action selectors to prevent infinite loops
export const useSetCurrentVitalSigns = () =>
  useVitalSignsStore((state) => state.setCurrentVitalSigns);
export const useSetVitalSignsHistory = () =>
  useVitalSignsStore((state) => state.setVitalSignsHistory);
export const useSetSelectedVitalSignsId = () =>
  useVitalSignsStore((state) => state.setSelectedVitalSignsId);
export const useAddVitalSigns = () => useVitalSignsStore((state) => state.addVitalSigns);
export const useUpdateVitalSigns = () => useVitalSignsStore((state) => state.updateVitalSigns);
export const useSetVitalSignsLoading = () => useVitalSignsStore((state) => state.setLoading);
export const useSetVitalSignsError = () => useVitalSignsStore((state) => state.setError);
export const useClearVitalSignsError = () => useVitalSignsStore((state) => state.clearError);
export const useResetVitalSignsStore = () => useVitalSignsStore((state) => state.reset);

// Legacy combined actions hook - use individual hooks above for better performance
export const useVitalSignsActions = () => ({
  setCurrentVitalSigns: useSetCurrentVitalSigns(),
  setVitalSignsHistory: useSetVitalSignsHistory(),
  setSelectedVitalSignsId: useSetSelectedVitalSignsId(),
  addVitalSigns: useAddVitalSigns(),
  updateVitalSigns: useUpdateVitalSigns(),
  setLoading: useSetVitalSignsLoading(),
  setError: useSetVitalSignsError(),
  clearError: useClearVitalSignsError(),
  reset: useResetVitalSignsStore(),
});

// Export the store for direct access if needed
export { useVitalSignsStore };
